import { toTitleCase } from "@/utils/format";

/**
 * getUniqueLabel function
 * This function takes a base label, an array of existing labels, an optional exclude label
 * and returns a unique label by appending a number if necessary.
 * If the base label already exists, it increments the number until a unique label is found.
 * e.g. getUniqueLabel('Logo', ['Logo', 'Logo 1', 'Logo 2']) => 'Logo 3'
 *
 * @param baseLabel - The desired label name
 * @param existingLabels - Array of existing labels to check against
 * @param excludeLabel - Optional label to exclude from the check (useful when renaming)
 */
export function getUniqueLabel(baseLabel: string, existingLabels: string[], excludeLabel?: string): string {
  // Trim whitespace from baseLabel to prevent duplicates like "Logo" and "Logo "
  const trimmedBaseLabel = baseLabel.trim();

  // Filter out the excludeLabel if provided (useful when renaming)
  const filteredLabels = excludeLabel ? existingLabels.filter((label) => label !== excludeLabel) : existingLabels;

  // Create lowercase versions for case-insensitive comparison
  const trimmedBaseLabelLower = trimmedBaseLabel.toLowerCase();
  const filteredLabelsLower = filteredLabels.map((label) => label.toLowerCase());

  // If the trimmed base label doesn't exist in filtered labels (case-insensitive), we can use it and return it
  if (!filteredLabelsLower.includes(trimmedBaseLabelLower)) {
    return toTitleCase(trimmedBaseLabel);
  }

  // Remove index from trimmedBaseLabel to avoid duplicates like "Logo 1" and "Logo 2"
  const baseLabelWithoutIndex = trimmedBaseLabel.replace(/\s\d+$/, "").trim();

  // Find the next available number by checking incrementally
  let counter = 1;
  let candidateLabel = `${baseLabelWithoutIndex} ${counter}`;

  while (filteredLabelsLower.includes(candidateLabel.toLowerCase())) {
    counter++;
    candidateLabel = `${baseLabelWithoutIndex} ${counter}`;
  }

  // Return the unique label in title case
  return toTitleCase(candidateLabel);
}
